import { useEffect, useRef, useState, type ReactNode } from "react";

import { useGetAsset } from "../../../api/generated/endpoints/assets/assets";
import { useGetJob } from "../../../api/generated/endpoints/generation/generation";
import type { GenerationOutput, SourceRef } from "../../../api/generated/model";
import { pictureOf, type PickedImage } from "../../../platform/media/ImagePicker";
import { useImageUpload, type ImageUpload } from "../../../platform/media/image-upload";
import { ReferencesDialog } from "../../../platform/media/ReferencesDialog";
import type { MediaRefs } from "../video/MediaRefsSlot";

export interface ToolVideo {
  /** The file the tool works on, with its picture (a video's poster), name and size if known. */
  video: PickedImage | null;
  /** As a request names it. */
  ref: SourceRef | null;
  /** The slot's state, for `MediaRefsSlot` (or `ImageRefsSlot` for an image). */
  slot: MediaRefs;
  /** Live's library dialog while it is open. */
  dialog: ReactNode;
  upload: ImageUpload;
  /** Sets the file another way: a product's photo for an image. */
  choose: (picked: PickedImage) => void;
}

/** Live's dialog words for each kind's one file (`video-refs-modal`, the image refs modal). */
const DIALOG = {
  video: { title: "Video References", subtitle: "Select up to 1" },
  image: {
    title: "Select Image References",
    subtitle: "Choose images to reference · Select up to 1",
  },
} as const;

/**
 * The one file of a kind a dedicated tool form works on (live's `VideoRefInput` and
 * `ImageRefsInput` at one file): from the library in live's dialog, or uploaded through the
 * library's one upload path.
 */
export function useToolMedia(
  teamId: string,
  brandId: string,
  kind: "video" | "image",
  /** A clone's file (`?clone=`): a library file or an earlier job's output, held until another
   *  is chosen or it is removed, read for its picture and name (issue 828, item 23). */
  initial: SourceRef | null = null,
): ToolVideo {
  const [chosen, setChosen] = useState<PickedImage | null>(null);
  const [held, setHeld] = useState<SourceRef | null>(initial);
  const heldAsset = held?.kind === "asset" ? held.asset_id : null;
  const read = useGetAsset(teamId, brandId, heldAsset ?? "", { query: { enabled: heldAsset !== null } }).data;
  const heldOutput = held?.kind === "job_output" ? held : null;
  const output = useGetJob(teamId, heldOutput?.job_id ?? "", {
    query: { enabled: heldOutput !== null },
  }).data?.outputs.find((each) => each.index === heldOutput?.output_index);
  const video: PickedImage | null =
    heldOutput !== null
      ? outputPicked(heldOutput, output, kind)
      : heldAsset === null
        ? chosen
        : read === undefined
          ? { assetId: heldAsset, url: null, name: kind === "video" ? "The video" : "The image" }
          : {
              assetId: read.id,
              url: kind === "video" ? (read.urls.poster ?? read.urls.thumb ?? null) : pictureOf(read),
              original: read.urls.original,
              name: read.name,
              width: read.width,
              height: read.height,
              durationMs: read.duration_ms,
              bytes: read.bytes,
            };
  const setVideo = (next: PickedImage | null) => {
    setHeld(null);
    setChosen(next);
  };
  const [picking, setPicking] = useState(false);
  const running = useRef<AbortController | null>(null);
  useEffect(() => () => running.current?.abort(), []);
  const upload = useImageUpload({
    teamId,
    brandId,
    kind,
    onUploaded: (asset) =>
      setVideo({
        assetId: asset.id,
        url: kind === "video" ? (asset.urls.poster ?? null) : pictureOf(asset),
        name: asset.name,
        width: asset.width,
        height: asset.height,
        durationMs: asset.duration_ms,
        bytes: asset.bytes,
      }),
    onUploading: (controller) => {
      running.current = controller;
    },
  });
  return {
    video,
    ref: heldOutput ?? (video === null ? null : { kind: "asset", asset_id: video.assetId }),
    slot: {
      items: video === null ? [] : [video],
      tagged: false,
      disabled: false,
      full: video !== null,
      // None here, as live's Dubbing, Lip-Sync and Edit Background forms show none; a form whose
      // live slot shows its mode's limits passes its own (Motion Control, issue 461).
      limits: [],
      onFromLibrary: () => setPicking(true),
      onUpload: (file) => void upload.send(file),
      uploading: upload.pending,
      onRemove: () => setVideo(null),
    },
    dialog: picking ? (
      <ReferencesDialog
        teamId={teamId}
        brandId={brandId}
        kind={kind}
        title={DIALOG[kind].title}
        subtitle={DIALOG[kind].subtitle}
        max={1}
        chosen={video === null || heldOutput !== null ? [] : [video.assetId]}
        onClose={() => setPicking(false)}
        onConfirm={(chosen) => setVideo(chosen[0] ?? null)}
      />
    ) : null,
    upload,
    choose: setVideo,
  };
}

/**
 * An earlier job's output as a slot shows it (a clone's): its thumb, a video's poster, once its
 * job is read. The slot keys its one item by `assetId`; an output is no library file, so the
 * library dialog lists no such id.
 */
function outputPicked(
  ref: { job_id: string; output_index: number },
  output: GenerationOutput | undefined,
  kind: "video" | "image",
): PickedImage {
  const still = kind === "video" ? (output?.poster_url ?? output?.thumb_url) : (output?.thumb_url ?? output?.url);
  return {
    assetId: `${ref.job_id}:${ref.output_index}`,
    url: still ?? null,
    original: output?.url ?? null,
    name: `${kind === "video" ? "Video" : "Image"} ${ref.output_index + 1} of an earlier job`,
    width: output?.width ?? null,
    height: output?.height ?? null,
    durationMs: output?.duration_ms ?? null,
  };
}

/** The one video a tool form works on (live's "Video" slot). */
export function useToolVideo(teamId: string, brandId: string): ToolVideo {
  return useToolMedia(teamId, brandId, "video");
}
